import React, { useCallback, useState } from 'react';
import Alert from 'react-bootstrap-sweetalert';
import Axios from 'axios';
import { envs } from 'redux/api/windowServerData';
import { replaceCpfCnpj } from '../../CreateUser/helpers/utils';
import styles from '../confirm.module.scss';
import { getConsumerCPF } from '../functions';
import { useLocation } from 'react-router-dom'
import { GetThemeColorFromStorage, GetThemeFromAPI } from '../../../redux/api/themeColor';
import { hideOnEcommerce, isEcommerce, mockUrlEcommerceLogin } from '../../../utils';

export function useConfirmRegistration(props) {
	const [themeColor, setThemeColor] = useState(GetThemeColorFromStorage())
	const [resetForm, setResetForm] = useState(false);
	const [isSent, setIsSent] = useState(false);
	const [loading, setLoading] = useState(false);
	const [alert, setAlert] = useState(null);
	const [consumer, setConsumer] = useState(null);
	const location = useLocation()
	const [loadingInitial, setLoadingInitial] = useState(false)

	const emailToken = location?.state?.userData?.data;

	const showLoader = () => {
		setLoading(true);
		setAlert(null);
	};

	const hideLoader = () => {
		setAlert(null);
		setLoading(false);
	};

	const getTheme = async () => {
		setLoadingInitial(true)
		try {
			const response = await GetThemeFromAPI()

			if (response?.status === 200 && response?.data) {
				setThemeColor(response.data)
			}
		} catch (error) {
			console.log('Error fetching theme:', error)
		} finally {
			setLoadingInitial(false)
		}
	}

	const closeLoader = () => setLoading(false);

	const renderAlert = (text, onConfirm, confirmText = 'OK', type = 'warning') => {
		setAlert(
			<Alert
				{...{ [type]: true }}
				style={{ display: 'block', marginTop: '-100px' }}
				title={null}
				onConfirm={onConfirm}
				confirmBtnCssClass={`${props.classes.button} ${props.classes.greenButtonColor}`}
				confirmBtnText={confirmText}
			>
				{text}
			</Alert>
		);
	};

	const getPhoneNumber = (user) => {
		if (!user?.cpf) return '';
		return user.cpf.length === 11 ? user.phoneNumber2 || '' : user.representative?.phoneNumber || '';
	};

	const redirectToLogin = () => {
		if (isEcommerce) {
			window.parent.postMessage({ action: 'redirect', url: mockUrlEcommerceLogin }, '*');
			return;
		}
		props.history.push('/auth/login');
	}

	const handleClose = () => {
		localStorage.removeItem('consumerSMS');
		setAlert(null);

		redirectToLogin();
	};


	const handleSendEmail = useCallback(async (cpf) => {
		showLoader();
		try {
			const response = await Axios.put(`/Consumer/SendEmailConfirmationToken?cpfCnpj=${cpf}`, {
				baseURL: envs.REACT_APP_GERMINI_API_URL,
			});

			if (Array.isArray(response?.data.errors)) {
				for (const { message } of response?.data.errors) {
					switch (message) {
						case 'token - INVALID_VALUE':
							return renderAlert('O Token de validação de e-mail está inválido.', () => redirectToLogin());
						case 'consumer - EMAIL_ALREADY_VALIDATED':
							return renderAlert(
								'Seu e-mail já foi validado. Você receberá um SMS com o código de confirmação.',
								() => sendSMSActivationCode(cpf),
								'Reenviar código'
							);

						case 'consumer - PHONENUMBER_ALREADY_VALIDATED':
							return renderAlert(props.t(message), handleClose, 'Realizar login');
						case 'consumer - EMAIL_TOKEN_EXPIRED':
							return renderAlert(
								'TOKEN EXPIRADO. Seu token de validação do cadastro expirou.',
								() => handleSendEmail(cpf),
								'Reenviar token'
							);
						default:
							return renderAlert('Ocorreu um erro ao realizar operação!', hideLoader);
					}
				}
			}

			localStorage.setItem('cpfUser', cpf)

			props.history.push({
				pathname: isEcommerce ? '/authenticateAccess/ecommerce' : `/authenticateAccess`,
			})

		} catch (error) {
			console.error(error);
		} finally {
			closeLoader();
		}
	}, [props, consumer]);

	const ValidateEmailToken = useCallback(async (token) => {
		showLoader();
		try {
			const { data, errors } = await props.ValidateEmailToken(token);
			if (data) setConsumer(data);

			if (data?.status?.value === 'SIMPLIFY') {
				redirectToLogin();
				return;
			}


			if (Array.isArray(errors)) {

				for (const { message } of errors) {
					switch (message) {
						case 'token - INVALID_VALUE':
							return renderAlert('O Token de validação de e-mail está inválido ou expirado.', () => redirectToLogin());
						case 'consumer - EMAIL_ALREADY_VALIDATED':
							if (data?.cpf) SendSmsCode(data.cpf);
							setAlert(null);
							break
						case 'consumer - PHONENUMBER_ALREADY_VALIDATED':
							return renderAlert(props.t(message), handleClose, 'Realizar login');
						case 'consumer - EMAIL_TOKEN_EXPIRED':
							return renderAlert(
								'TOKEN EXPIRADO. Seu token de validação do cadastro expirou.',
								() => handleSendEmail(data?.cpf),
								'Reenviar token'
							);
						default:
							return renderAlert('Ocorreu um erro ao realizar operação!', hideLoader);
					}
				}
			}

		} catch (error) {
			console.error(error);
			renderAlert('Ocorreu um erro ao realizar operação!', hideLoader);
		} finally {
			closeLoader();
		}
	}, [handleSendEmail, props]);

	const SendSmsCode = useCallback(async (cpfCnpj) => {
		showLoader();

		try {
			const cpf = replaceCpfCnpj(cpfCnpj ?? getConsumerCPF() ?? "");
			const response = await props.SendSmsCode(cpf);

			if (!response?.success) {
				return renderAlert(
					Array.isArray(response?.errors)
						? response.errors.map((e, idx) => <div key={idx}>{props.t(e.message)}</div>)
						: 'Ocorreu um erro enviar o código de autenticação!',
					() => {
						if (!Array.isArray(response?.errors)) return window.location.reload();
						redirectToLogin();
					},
					Array.isArray(response?.errors) ? 'OK' : 'Reenviar código'
				);
			}

			if (consumer) {
				renderAlert(
					<>
						<div>
							{isSent
								? 'Um novo código foi enviado para o celular cadastrado:'
								: 'O código de autenticação foi enviado para o celular cadastrado:'}
						</div>
						<div className={styles.phoneNumberAlert}>{getPhoneNumber(consumer)}</div>
					</>,
					hideLoader,
					'OK',
					'success'
				);
				setResetForm(true);
			}

		} catch (error) {
			console.error(error);
			renderAlert('Ocorreu um erro ao enviar o SMS!', hideLoader);
		} finally {
			closeLoader();
		}
	}, [consumer, isSent, props]);

	const handleSave = async ({ numberToken }) => {
		showLoader();

		try {
			const cpfOrCNPJ = consumer?.cpf ?? getConsumerCPF() ?? ""
			const response = await props.ValidateSmsCode(cpfOrCNPJ, numberToken);

			if (!response?.success) {
				return renderAlert(
					Array.isArray(response?.errors)
						? response.errors.map((e, idx) => <div key={idx}>{props.t(e.message)}</div>)
						: 'Ocorreu um erro ao realizar operação!',
					hideLoader
				);
			}

			setAlert(null);
			props.history.push(isEcommerce ? '/success/ecommerce' : '/success');

		} catch (error) {
			console.error(error);
		} finally {
			closeLoader();
		}
	};

	const requestSmsCode = async () => {
		showLoader();
		try {
			const cpf = consumer?.cpf ?? getConsumerCPF() ?? "";

			if (cpf) await SendSmsCode(cpf);
		} catch (error) {
			console.error(error);
		} finally {
			closeLoader();
		}
	};

	const sendSMSActivationCode = async (cpfCnpj) => {
		showLoader();
		const cpf = replaceCpfCnpj(cpfCnpj);

		try {
			const response = await Axios.put(
				`/Consumer/SendSmsActivationCode?cpfCnpj=${cpf}`,
				{ baseURL: envs.REACT_APP_GERMINI_API_URL }
			);

			if (!response?.data?.success) {
				return renderAlert(
					Array.isArray(response?.data?.errors)
						? response.errors.map((e, idx) => <div key={idx}>{props.t(e.message)}</div>)
						: 'Ocorreu um erro enviar o código de autenticação!',
					() => {
						if (!Array.isArray(response?.data?.errors)) return window.location.reload();
						redirectToLogin();
					},
					Array.isArray(response?.data?.errors) ? 'OK' : 'Reenviar código'
				);
			}

			localStorage.setItem(
				'consumerSMS',
				JSON.stringify({ consumer: response.data.data, expireDate: Date.now() + 900000 })
			);
			props.history.push(isEcommerce ? '/confirmRegistration/ecommerce' : '/ConfirmRegistration');

			setIsSent(true);
			setAlert(null);
		} catch (error) {
			console.error(error);
		} finally {
			closeLoader();
		}
	};

	const handleSent = () => setIsSent(false)

	return {
		hideOnEcommerce,
		isEcommerce,
		sendSMSActivationCode,
		handleClose,
		handleSave,
		requestSmsCode,
		handleSendEmail,
		hideLoader,
		closeLoader,
		showLoader,
		setAlert,
		getPhoneNumber,
		resetForm,
		isSent,
		loading,
		alert,
		consumer,
		ValidateEmailToken,
		handleSent,
		loadingInitial,
		getTheme,
		themeColor,
		emailToken,
	};
}
